// Moved from `src/hooks/mutation/useMutateUpdateUser.ts` (now the app delegate)
// with the identity D2 slice (ARCFE-1062). Unblocked by D1 (ARCFE-1037), which
// carved `updateUser` and `updateUserName` into `../api/user`.
//
// THE TWO LEGS THE FAMILY NOTE NAMED AS BLOCKERS ARE BOTH ORDINARY DELEGATE
// PORTS, and re-measured at HEAD that is all they ever were (the OT4 correction,
// ARCFE-1033):
//   - `bulkSyncBridge.invalidateNoteDefaultsCache()` behind `isTauri()` is a
//     DESKTOP PLATFORM capability. `isTauri()` is an app predicate, so the
//     platform half of the guard sits at the delegate; the `noteDefaultsChanged`
//     half is a data branch and stays here. Both are pure predicates, so
//     splitting the `&&` preserves the legacy short-circuit exactly.
//   - `invalidateCurrentUserBillingInfo()` is COMMERCE. Per delivery section 3
//     the owning domain exports the command and app integration sequences it —
//     never injected through `configureWorkspaceData` (design D7's hidden-edge
//     ban).
// `invalidateCurrentUser()` crosses for the third reason recorded at OT4: it is
// COMPOSITE (GET_USER *and* the commerce GET_PRODUCTS behind one try/catch), so
// reimplementing its workspace leg here would silently drop a commerce
// invalidation. All three sit together in one port per lifecycle position.
//
// What stays here because it is workspace's own: the `GET_USER` optimistic deep
// merge, the language-code normalisation in the mutationFn, and the
// `GET_JOINED_TEAMS` invalidation.
import { logger } from '@heidi/runtime/logger';
import { LanguageCode } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_JOINED_TEAMS,
  GET_USER,
  GET_WORKSPACES,
  type UpdateUserRequest,
  type UserResponse,
} from '../../contracts';
import { updateUser, updateUserName } from '../api/user';

const UPDATE_USER = 'updateUser';

export interface UseMutateUpdateUserOptions {
  /**
   * Mutation-level success port. Unlike callbacks passed to `mutate`, this still
   * fires if the calling component unmounts while the request is in flight.
   */
  onUpdateUserSuccess?: (variables: UpdateUserRequest) => void;
  /**
   * App-reaction port: fired where the `invalidateCurrentUser()` →
   * `invalidateCurrentUserBillingInfo()` pair fired in `onSuccess`, in that
   * order and BEFORE the joined-teams invalidation this hook still owns.
   */
  onUserUpdated?: () => void;
  /**
   * App-reaction port: fired in `onSuccess` when a cached note-defaults field
   * changed (`default_template_id`, `default_voice_style` or `brain`). The
   * DESKTOP half of the legacy `noteDefaultsChanged && isTauri()` guard belongs
   * to the delegate, which owns `isTauri()` and the bulk-sync bridge.
   */
  onNoteDefaultsChanged?: () => void;
  /**
   * App-reaction port: fired where the same `invalidateCurrentUser()` →
   * `invalidateCurrentUserBillingInfo()` pair fired in `onError`, after the log.
   */
  onUpdateUserError?: () => void;
}

/** PATCH /user/ (+ PATCH /user/name when a name field is present). */
export function useMutateUpdateUser({
  enableInvalidation = true,
  ...options
}: { enableInvalidation?: boolean } & UseMutateUpdateUserOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    retry: 3,
    mutationKey: [UPDATE_USER],
    mutationFn: async (data: UpdateUserRequest) => {
      if (data.first_name || data.last_name) {
        await updateUserName({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
        });
      }

      /**
       * Processes user update data with special handling for language code settings.
       * This ensures consistency between single and multiple language code fields:
       *
       * Three scenarios are handled:
       * 1. Multiple language codes:
       *    - If default_input_language_codes array exists and has more than one language,
       *      set default_input_language_code to 'AUTO'
       *    Example:
       *    Input:  { default_input_language_codes: ['en', 'es'] }
       *    Output: {
       *      default_input_language_codes: ['en', 'es'],
       *      default_input_language_code: 'AUTO'
       *    }
       *
       * 2. Multiple language codes (single language):
       *    - If default_input_language_codes array exists with one language,
       *      set default_input_language_code to that language
       *    Example:
       *    Input:  { default_input_language_codes: ['en'] }
       *    Output: {
       *      default_input_language_codes: ['en'],
       *      default_input_language_code: 'en'
       *    }
       *
       * 3. Single language code:
       *    - If only default_input_language_code exists, keep it and create
       *      default_input_language_codes as an array containing that single code
       *    Example:
       *    Input:  { default_input_language_code: 'en' }
       *    Output: {
       *      default_input_language_code: 'en',
       *      default_input_language_codes: ['en']
       *    }
       */
      const updateData: UpdateUserRequest = {
        ...data,
        session_settings: data.session_settings && {
          ...data.session_settings,
          ...(Array.isArray(data.session_settings.default_input_language_code_list) && {
            default_input_language_code_list:
              data.session_settings.default_input_language_code_list,
            default_input_language_code:
              data.session_settings.default_input_language_code_list.length > 1
                ? LanguageCode.auto
                : data.session_settings.default_input_language_code_list[0],
          }),
          ...(data.session_settings.default_input_language_code &&
            !data.session_settings.default_input_language_code_list && {
              default_input_language_code:
                data.session_settings.default_input_language_code,
              default_input_language_codes: [
                data.session_settings.default_input_language_code,
              ],
            }),
        },
      };

      return await updateUser(updateData);
    },
    onMutate: (data) => {
      queryClient.setQueryData([GET_USER], (oldData: UserResponse) => {
        // Deep merge nested objects to preserve existing values
        return {
          ...oldData,
          ...data,
          // Merge session_settings if provided
          ...(data.session_settings && {
            session_settings: {
              ...oldData?.session_settings,
              ...data.session_settings,
            },
          }),
          // Merge evidence_settings if provided
          ...(data.evidence_settings && {
            evidence_settings: {
              ...oldData?.evidence_settings,
              ...data.evidence_settings,
            },
          }),
          // Merge dictation deeply so updating enabled.value preserves languages/hotkeys.
          ...(data.dictation && {
            dictation: {
              ...oldData?.dictation,
              ...data.dictation,
              ...(data.dictation.enabled && {
                enabled: {
                  ...oldData?.dictation?.enabled,
                  ...data.dictation.enabled,
                },
              }),
            },
          }),
        };
      });
    },
    onSuccess: (_, variables) => {
      options?.onUpdateUserSuccess?.(variables);

      // Some update requests don't require a re-fetch of the user data & billing info
      if (variables?.should_show_section_highlight_in_template !== undefined) {
        return;
      }

      if (enableInvalidation) {
        options?.onUserUpdated?.();

        // BE may auto-create Practice team on non-solo org-size PATCH.
        // Covers onboarding v2, legacy survey modal, invite-team step.
        if (variables?.organisation_info !== undefined) {
          void queryClient.invalidateQueries({ queryKey: [GET_JOINED_TEAMS] });
          void queryClient.invalidateQueries({ queryKey: [GET_WORKSPACES] });
        }

        // Invalidate the bulk-sync engine's cached note defaults when any
        // cached field changes. Desktop-only — the delegate's `isTauri()`
        // short-circuits on web. The Rust cache stores default_template_id,
        // default_voice_style, and brain; refreshes on the next bulk-sync.
        const sessionSettings = variables?.session_settings;
        const noteDefaultsChanged =
          sessionSettings &&
          (sessionSettings.default_template_id !== undefined ||
            sessionSettings.default_voice_style !== undefined ||
            sessionSettings.brain !== undefined);
        if (noteDefaultsChanged) {
          options?.onNoteDefaultsChanged?.();
        }
      }
    },
    onError: (error) => {
      logger.error('Failed to update user preference:', error);
      options?.onUpdateUserError?.();
    },
  });
}
